import { lazy, Suspense, useState, useEffect, useLayoutEffect } from "react";
import { ThemeProvider } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { DarkModeSwitch } from "react-toggle-dark-mode";
import { useTranslation } from "react-i18next";

import useDarkMode from "./hooks/useDarkMode";
import useNetworkStatus from "./hooks/useNetworkStatus";
import LanguagesButton from "./components/LanguagesButton/LanguagesButton";
import Loading from "./components/Loading/Loading";
import LogoImage from "./components/LogoImage/LogoImage";
import Image from "./assets/images/logo.png";
import LogoDescription from "./components/LogoDescription/LogoDescription";
import CopyRight from "./components/CopyRight/CopyRight";
import ToastNetworkStatus from "./components/ToastMessages/ToastNetworkStatus";
import JumperButton from "./components/JumperButton/JumperButton";

import "./App.css";

import { inject } from "@vercel/analytics";
inject();

// Routing component is a little bit heavy to load, so using lazy function to load this component
const Routing = lazy(() => import("./components/Routing/Routing"));

const date = new Date(); // Need a date for footer copyright date

const App = () => {
  const [scrollTop, setScrollTop] = useState(); // Hook for scrollbar position
  const [theme, themeToggler] = useDarkMode(); // Custom hook for toggle theme
  const { t, i18n } = useTranslation(); // Library hook for translating text
  const isOnline = useNetworkStatus(); // Custom hook for watching network status

  // Using this variable to change app direction dynamically
  const selectedLanguageDirection = i18n.dir();

  // Change body class name when toggle theme
  useLayoutEffect(() => {
    document.body.className = theme === "dark" ? "dark" : "";
  }, [theme]);

  // Change body direction and language by i18n property and method
  useEffect(() => {
    const bootstrapCDN = document.getElementById("bootstrap-cdn"); // <Link> tag inside head whithin index.html

    document.body.dir = i18n.dir(); // Changing body direction
    document.body.lang = i18n.language; // Changing body language

    // Check current language and set a proper CDN source address for CSS reference link
    if (i18n.language === "en") {
      bootstrapCDN.setAttribute("href", "https://cdn.jsdelivr.net/npm/bootstrap@5.2.3/dist/css/bootstrap.min.css");
    } else {
      bootstrapCDN.setAttribute("href", "https://cdn.jsdelivr.net/npm/bootstrap@5.2.3/dist/css/bootstrap.rtl.min.css");
    }
  }, [i18n, selectedLanguageDirection]);

  // First initializing at startup
  useEffect(() => {
    const svgContainer = document.getElementById("svg"); // SVG container
    const svgAnime = document.getElementById("svg-anime"); // SVG container animation part

    const mouseEnterHandler = () => {
      svgAnime.setAttribute("dur", "2s"); // Changing SVG animation duration
    };

    const mouseLeaveHandler = () => {
      svgAnime.setAttribute("dur", "20s"); // Changing SVG animation duration
    };

    const scrollHandler = () => {
      setScrollTop(window.scrollY); // Get the window scroll value to show or hide the jumper button
    };

    window.addEventListener("scroll", scrollHandler);

    // Set two event listeners on SVG to handle the mouse hover action
    svgContainer.addEventListener("mouseenter", mouseEnterHandler);
    svgContainer.addEventListener("mouseleave", mouseLeaveHandler);

    // Removing all event listeners to prevent memory leaks
    return () => {
      window.removeEventListener("scroll", scrollHandler);
      svgContainer.removeEventListener("mouseenter", mouseEnterHandler);
      svgContainer.removeEventListener("mouseleave", mouseLeaveHandler);
    };
  }, []);

  return (
    // ThemeProvider with correct dir props is necessary for multilanguage apps
    <ThemeProvider dir={i18n.dir()}>
      {/* Show a Toast when the user is offline and back online */}
      <ToastNetworkStatus isOnline={isOnline} delayTime={5000} />

      {/* Show or hide the jumper button depends on the scroll value */}
      {scrollTop > 200 ? <JumperButton buttonColor="danger" /> : null}

      <section id="option-section" className="option-section">
        <LanguagesButton theme={theme} />

        <DarkModeSwitch
          className="theme-btn"
          checked={theme === "dark" ? true : false}
          onChange={themeToggler}
          size={"2rem"}
          sunColor="yellow"
          moonColor="whitesmoke"
        />
      </section>
      
      <Container fluid className="mt-5 py-3 p-sm-3 p-md-4 p-lg-5">
        <header>
          <Row className="mb-5 justify-content-center align-items-center">
            <LogoImage xs={3} sm={2} lg={1} imageAdress={Image} imageWidth={80} imageHeight={80} />
            <LogoDescription xs={9} sm={10} lg={11} fullName={t("fullName")} jobPosition={t("jobPosition")} />
          </Row>
        </header>

        <main>
          <Row>
            {/* A suspense wrapper is needed because we use the lazy function to load a heavy component */}
            <Suspense fallback={<Loading loadingColor="warning" fontSize="1" loadingText={t("loadingText")} />}>
              <Routing />
            </Suspense>
          </Row>
        </main>

        <footer>
          <Row className="text-center">
            <CopyRight date={date} developerName={t("fullName")} />
          </Row>
        </footer>
      </Container>
    </ThemeProvider>
  );
};

export default App;
